import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import { ArrowRightIcon } from "@phosphor-icons/react";

const HERO_BG = "https://images.unsplash.com/photo-1758868105374-d0875c0496bf?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwzfHxhYnN0cmFjdCUyMGFyY2hpdGVjdHVyYWwlMjB0ZXh0dXJlJTIwbWluaW1hbGlzdHxlbnwwfHx8fDE3Nzc4MTM0NDN8MA&ixlib=rb-4.1.0&q=85";

export default function Login({ mode = "login" }) {
    const isSignup = mode === "signup";
    const navigate = useNavigate();
    const { login, signup } = useAuth();
    const [form, setForm] = useState({ email: "", password: "", name: "", company: "" });
    const [busy, setBusy] = useState(false);

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            if (isSignup) {
                await signup({ email: form.email, password: form.password, name: form.name, company: form.company });
                toast.success("Workspace created. Sample data loaded.");
            } else {
                await login(form.email, form.password);
                toast.success("Welcome back.");
            }
            navigate("/dashboard");
        } catch (err) {
            const msg = err?.response?.data?.detail || "Authentication failed";
            toast.error(typeof msg === "string" ? msg : "Authentication failed");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="min-h-screen grid lg:grid-cols-2">
            {/* Brand panel */}
            <div className="relative hidden lg:flex flex-col justify-between p-10 text-white overflow-hidden" data-testid="auth-hero-panel">
                <img
                    src={HERO_BG}
                    alt=""
                    aria-hidden="true"
                    className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-black/55" aria-hidden="true" />
                <div className="relative z-10">
                    <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 bg-white text-black flex items-center justify-center font-mono text-[12px] font-bold">N*</div>
                        <div>
                            <div className="font-bold tracking-tight">NORTHSTAR</div>
                            <div className="overline text-[9px] text-white/70 mt-0.5">Sales Calling OS</div>
                        </div>
                    </div>
                </div>
                <div className="relative z-10 max-w-md">
                    <div className="overline text-white/70 mb-5">CONTROL ROOM, NOT A CRM</div>
                    <h2 className="text-4xl font-bold tracking-tight leading-tight">
                        Outbound at the speed of intent.
                    </h2>
                    <p className="text-[15px] text-white/80 mt-4 leading-relaxed">
                        Upload a prospect list, drop in a script, and deploy AI agents to qualify
                        meetings while your team works on closes that matter.
                    </p>
                    <div className="mt-8 grid grid-cols-3 gap-4">
                        {[
                            { k: "Calls/day", v: "10×" },
                            { k: "Lift in book rate", v: "+38%" },
                            { k: "Time to first meeting", v: "<24h" },
                        ].map((s) => (
                            <div key={s.k}>
                                <div className="mono text-2xl font-semibold">{s.v}</div>
                                <div className="overline text-white/70 text-[9px] mt-1">{s.k}</div>
                            </div>
                        ))}
                    </div>
                </div>
                <div className="relative z-10 overline text-white/60">
                    EST. 2026 · TCPA-AWARE · DNC-CHECKED
                </div>
            </div>

            {/* Form panel */}
            <div className="flex items-center justify-center p-8 sm:p-12">
                <form onSubmit={submit} className="w-full max-w-md" data-testid="auth-form">
                    <div className="overline mb-3">{isSignup ? "CREATE WORKSPACE" : "SIGN IN"}</div>
                    <h1 className="text-3xl font-bold tracking-tight">
                        {isSignup ? "Spin up your control room." : "Welcome back."}
                    </h1>
                    <p className="text-[14px] text-[var(--text-secondary)] mt-2">
                        {isSignup
                            ? "We'll seed a sample list and three scripts so you can see it run."
                            : "Sign in to monitor live campaigns."}
                    </p>

                    <div className="mt-7 space-y-4">
                        {isSignup && (
                            <>
                                <Field label="Full name" testid="signup-name">
                                    <input
                                        required
                                        value={form.name}
                                        onChange={set("name")}
                                        className="auth-input"
                                        data-testid="signup-name-input"
                                        placeholder="Alex Pereira"
                                    />
                                </Field>
                                <Field label="Company">
                                    <input
                                        value={form.company}
                                        onChange={set("company")}
                                        className="auth-input"
                                        data-testid="signup-company-input"
                                        placeholder="Northstar Research"
                                    />
                                </Field>
                            </>
                        )}
                        <Field label="Email">
                            <input
                                required
                                type="email"
                                value={form.email}
                                onChange={set("email")}
                                className="auth-input"
                                data-testid="auth-email-input"
                                placeholder="you@company.com"
                            />
                        </Field>
                        <Field label="Password">
                            <input
                                required
                                type="password"
                                minLength={6}
                                value={form.password}
                                onChange={set("password")}
                                className="auth-input"
                                data-testid="auth-password-input"
                                placeholder="At least 6 characters"
                            />
                        </Field>
                    </div>

                    <button
                        type="submit"
                        disabled={busy}
                        data-testid="auth-submit-button"
                        className="btn-primary w-full mt-6 px-4 py-3 flex items-center justify-center gap-2 text-[14px] disabled:opacity-50"
                    >
                        {busy ? "Working…" : isSignup ? "Create workspace" : "Sign in"}
                        <ArrowRightIcon size={14} weight="bold" />
                    </button>

                    <div className="mt-6 text-[13px] text-[var(--text-secondary)]">
                        {isSignup ? (
                            <>Already have an account? <Link to="/login" className="text-[var(--text-primary)] underline" data-testid="link-to-login">Sign in</Link></>
                        ) : (
                            <>Need an account? <Link to="/signup" className="text-[var(--text-primary)] underline" data-testid="link-to-signup">Create workspace</Link></>
                        )}
                    </div>
                </form>
            </div>

            <style>{`
                .auth-input {
                    width: 100%;
                    padding: 11px 12px;
                    border: 1px solid var(--border);
                    border-radius: 2px;
                    font-family: 'IBM Plex Sans', sans-serif;
                    font-size: 14px;
                    background: #fff;
                    transition: border-color 150ms ease-out, box-shadow 150ms ease-out;
                }
                .auth-input:focus {
                    outline: none;
                    border-color: var(--accent);
                    box-shadow: 0 0 0 2px rgba(0, 51, 160, 0.18);
                }
            `}</style>
        </div>
    );
}

const Field = ({ label, children }) => (
    <label className="block">
        <span className="overline mb-1.5 block">{label}</span>
        {children}
    </label>
);
